import React, { useState, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Paper, LinearProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Chip, CircularProgress,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import { useMsal } from '@azure/msal-react';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

const API_BASE = `${AI_ENGINE_URL}/api/v1/video-search`;
const ADMIN_EMAILS = ['joris@fullframe.nl', 'ton@fullframe.nl'];
const MAX_FILES = 10;
const MAX_SIZE = 2 * 1024 * 1024 * 1024; // 2GB

const CHIP_SX = { fontSize: '0.65rem', height: 20 };
const ANALYSIS_LABEL = {
  completed: 'geanalyseerd',
  processing: 'bezig',
  pending: 'in wachtrij',
  failed: 'mislukt',
  skipped: 'overgeslagen',
  unprocessed: 'nog niet geanalyseerd',
};
const ANALYSIS_COLOR = { completed: 'success', failed: 'error', processing: 'secondary' };

const estimateCostEur = (files) => {
  let totalUsd = 0;
  for (const f of files) {
    // Estimate ~1 min per 10MB if no duration known
    const durEstimate = f.duration || (f.file.size / (10 * 1024 * 1024)) * 60;
    const audioCost = (durEstimate / 60) * 0.003;
    const visualCost = (durEstimate / 5) * 0.001;
    totalUsd += audioCost + visualCost;
  }
  return totalUsd * 0.92;
};

const formatSize = (bytes) => {
  if (bytes > 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function VideoUpload() {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const userEmail = activeAccount?.username?.toLowerCase() || '';
  const isAdmin = ADMIN_EMAILS.includes(userEmail);

  const [files, setFiles] = useState([]); // [{file, status, progress, error, videoId, duration}]
  const [clientName, setClientName] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [lookupQuery, setLookupQuery] = useState('');
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [lookupResults, setLookupResults] = useState(null);
  const [analyzingIds, setAnalyzingIds] = useState([]);

  const handleLookup = async () => {
    const q = lookupQuery.trim();
    if (q.length < 2) return;
    setLookupBusy(true);
    setLookupError(null);
    setLookupResults(null);
    try {
      const resp = await authFetch(`${API_BASE}/resolve?q=${encodeURIComponent(q)}`);
      if (!resp.ok) {
        const body = await resp.json().catch(() => null);
        throw new Error(body?.detail || `HTTP ${resp.status}`);
      }
      setLookupResults(await resp.json());
    } catch (e) {
      setLookupError(e.message);
    } finally {
      setLookupBusy(false);
    }
  };

  const handleAnalyseMatch = async (match) => {
    setAnalyzingIds(prev => [...prev, match.id]);
    setLookupError(null);
    try {
      const resp = await authFetch(`${API_BASE}/process/single/${match.id}`, { method: 'POST' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      if (data.error) throw new Error(data.error);
      setLookupResults(prev => prev && {
        ...prev,
        matches: prev.matches.map(m => m.id === match.id ? { ...m, analysis_status: 'completed' } : m),
      });
    } catch (e) {
      setLookupError(`Analyse van "${match.filename}" mislukt: ${e.message}`);
    } finally {
      setAnalyzingIds(prev => prev.filter(id => id !== match.id));
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      status: 'ready', // ready, uploading, analyzing, done, error
      progress: 0,
      error: null,
      videoId: null,
      duration: null,
    }));
    setFiles(prev => [...prev, ...newFiles].slice(0, MAX_FILES));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'video/x-matroska': ['.mkv'],
      'video/x-msvideo': ['.avi'],
      'video/webm': ['.webm'],
    },
    maxFiles: MAX_FILES,
    maxSize: MAX_SIZE,
    disabled: processing,
  });

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const readyFiles = files.filter(f => f.status === 'ready');
  const estimatedCost = estimateCostEur(readyFiles);
  const costExceedsLimit = estimatedCost > 5;

  const handleStartUpload = () => {
    if (readyFiles.length === 0) return;
    setCostDialogOpen(true);
  };

  const handleConfirmAndProcess = async () => {
    setCostDialogOpen(false);
    setProcessing(true);
    setError(null);

    for (let i = 0; i < files.length; i++) {
      if (files[i].status !== 'ready') continue;

      // Update status to uploading
      setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'uploading', progress: 10 } : f));

      try {
        const formData = new FormData();
        formData.append('file', files[i].file);
        formData.append('client_name', clientName);
        formData.append('project_number', projectNumber);

        // Upload
        setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, progress: 30 } : f));

        const uploadResp = await authFetch(`${API_BASE}/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResp.ok) {
          const err = await uploadResp.text();
          throw new Error(err || `HTTP ${uploadResp.status}`);
        }

        const uploadData = await uploadResp.json();
        const videoId = uploadData.video_id;

        // Update to analyzing
        setFiles(prev => prev.map((f, idx) => idx === i
          ? { ...f, status: 'analyzing', progress: 50, videoId, duration: uploadData.duration }
          : f
        ));

        // Process
        const processResp = await authFetch(`${API_BASE}/process/single/${videoId}`, {
          method: 'POST',
        });

        if (!processResp.ok) throw new Error(`Analysis HTTP ${processResp.status}`);

        const processData = await processResp.json();
        if (processData.error) throw new Error(processData.error);

        // Done
        setFiles(prev => prev.map((f, idx) => idx === i
          ? { ...f, status: 'done', progress: 100 }
          : f
        ));
      } catch (e) {
        setFiles(prev => prev.map((f, idx) => idx === i
          ? { ...f, status: 'error', error: e.message }
          : f
        ));
      }
    }

    setProcessing(false);
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'done': return <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />;
      case 'error': return <ErrorIcon color="error" sx={{ fontSize: 20 }} />;
      case 'uploading': return <CircularProgress size={18} />;
      case 'analyzing': return <CircularProgress size={18} color="secondary" />;
      default: return <VideoFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />;
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'uploading': return 'Uploaden...';
      case 'analyzing': return 'Analyseren...';
      case 'done': return 'Klaar';
      case 'error': return 'Mislukt';
      default: return 'Klaar voor upload';
    }
  };

  return (
    <Box>
      {/* Frame.io-link of projectnummer: video's uit onze eigen Frame.io zijn al
          gescand, dus opzoeken i.p.v. opnieuw uploaden (voorkomt duplicaten). */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
          Staat de video al in Frame.io?
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Plak een Frame.io-link of typ een projectnummer (bv. 8553)"
            value={lookupQuery}
            onChange={(e) => setLookupQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleLookup(); }}
            disabled={lookupBusy || processing}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} /> }}
          />
          <Button
            variant="outlined"
            onClick={handleLookup}
            disabled={lookupBusy || processing || lookupQuery.trim().length < 2}
            sx={{ flexShrink: 0 }}
          >
            {lookupBusy ? <CircularProgress size={20} /> : 'Zoeken'}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Zo voorkom je een duplicaat: alles uit onze Frame.io-account is al ingelezen.
        </Typography>

        {lookupError && <Alert severity="warning" sx={{ mt: 1.5 }}>{lookupError}</Alert>}

        {lookupResults && (
          lookupResults.matches.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Niets gevonden voor deze {lookupResults.matched_as}. Staat de video niet in onze
              Frame.io-account? Upload 'm dan hieronder als bestand.
            </Alert>
          ) : (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {lookupResults.count} treffer{lookupResults.count === 1 ? '' : 's'} via {lookupResults.matched_as}
              </Typography>
              <Paper variant="outlined" sx={{ mt: 0.5 }}>
                {lookupResults.matches.map((m, i) => (
                  <Box
                    key={m.id}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.25,
                      borderBottom: i < lookupResults.matches.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <VideoFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap fontWeight={500}>{m.filename}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {[m.client_name, m.project_number].filter(Boolean).join(' · ')}
                        </Typography>
                        {m.version_number != null && (
                          <Chip size="small" variant="outlined" label={`v${m.version_number}`} sx={CHIP_SX} />
                        )}
                        {m.language && <Chip size="small" variant="outlined" label={m.language} sx={CHIP_SX} />}
                        {m.variant && <Chip size="small" variant="outlined" label={m.variant} sx={CHIP_SX} />}
                        {m.is_current_version && (
                          <Chip size="small" color="primary" variant="outlined" label="laatste versie" sx={CHIP_SX} />
                        )}
                        <Chip
                          size="small"
                          variant="outlined"
                          color={ANALYSIS_COLOR[m.analysis_status] || 'default'}
                          label={ANALYSIS_LABEL[m.analysis_status] || m.analysis_status}
                          sx={CHIP_SX}
                        />
                      </Box>
                    </Box>
                    {m.analysis_status === 'completed' ? (
                      <Chip size="small" icon={<CheckCircleIcon />} color="success" variant="outlined" label="doorzoekbaar" />
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={analyzingIds.includes(m.id)}
                        onClick={() => handleAnalyseMatch(m)}
                        sx={{ flexShrink: 0 }}
                      >
                        {analyzingIds.includes(m.id) ? <CircularProgress size={16} /> : 'Analyseer'}
                      </Button>
                    )}
                  </Box>
                ))}
              </Paper>
            </Box>
          )
        )}
      </Paper>

      {/* Metadata fields */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          size="small"
          label="Klant (optioneel)"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          disabled={processing}
          sx={{ width: 250 }}
        />
        <TextField
          size="small"
          label="Projectnummer (optioneel)"
          value={projectNumber}
          onChange={(e) => setProjectNumber(e.target.value)}
          disabled={processing}
          sx={{ width: 200 }}
        />
      </Box>

      {/* Dropzone */}
      <Paper
        {...getRootProps()}
        variant="outlined"
        sx={{
          p: 4,
          mb: 3,
          textAlign: 'center',
          cursor: processing ? 'default' : 'pointer',
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          bgcolor: isDragActive ? 'primary.50' : 'transparent',
          transition: 'all 0.2s',
          '&:hover': processing ? {} : {
            borderColor: 'primary.main',
            bgcolor: 'grey.50',
          },
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" fontWeight={500}>
          {isDragActive ? 'Laat los om te uploaden' : 'Sleep video\'s hierheen of klik om te selecteren'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          MP4, MOV, MKV, AVI, WebM - max {MAX_FILES} bestanden, max 2GB per bestand
        </Typography>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* File list */}
      {files.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          {files.map((f, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5,
                borderBottom: i < files.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              {statusIcon(f.status)}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap fontWeight={500}>
                  {f.file.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatSize(f.file.size)}
                  </Typography>
                  <Chip label={statusLabel(f.status)} size="small" variant="outlined"
                    color={f.status === 'done' ? 'success' : f.status === 'error' ? 'error' : 'default'}
                    sx={{ fontSize: '0.65rem', height: 20 }}
                  />
                  {f.error && (
                    <Typography variant="caption" color="error.main" noWrap sx={{ maxWidth: 300 }}>
                      {f.error}
                    </Typography>
                  )}
                </Box>
                {(f.status === 'uploading' || f.status === 'analyzing') && (
                  <LinearProgress
                    variant={f.status === 'analyzing' ? 'indeterminate' : 'determinate'}
                    value={f.progress}
                    sx={{ mt: 0.5, height: 3, borderRadius: 2 }}
                  />
                )}
              </Box>
              {f.status === 'ready' && !processing && (
                <IconButton size="small" onClick={() => removeFile(i)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Paper>
      )}

      {/* Action buttons */}
      {files.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={processing ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            disabled={readyFiles.length === 0 || processing}
            onClick={handleStartUpload}
          >
            Upload & analyseer ({readyFiles.length} video{readyFiles.length !== 1 ? "'s" : ''})
          </Button>
          {!processing && (
            <Button
              variant="text"
              color="inherit"
              onClick={() => setFiles([])}
            >
              Alles verwijderen
            </Button>
          )}
          {files.some(f => f.status === 'done') && (
            <Typography variant="body2" color="success.main" sx={{ ml: 'auto' }}>
              {files.filter(f => f.status === 'done').length} van {files.length} voltooid
            </Typography>
          )}
        </Box>
      )}

      {/* Cost Dialog */}
      <Dialog open={costDialogOpen} onClose={() => setCostDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Upload & analyse starten</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>{readyFiles.length}</strong> video{readyFiles.length !== 1 ? "'s" : ''} uploaden en analyseren
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Totale grootte: <strong>{formatSize(readyFiles.reduce((sum, f) => sum + f.file.size, 0))}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Geschatte analysekosten: <strong>{'\u20AC'}{estimatedCost.toFixed(2)}</strong>
          </Typography>
          {costExceedsLimit && !isAdmin && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Kosten boven {'\u20AC'}5 - alleen admin mag dit starten.
              Neem contact op met Joris of Ton.
            </Alert>
          )}
          {costExceedsLimit && isAdmin && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Kosten boven {'\u20AC'}5 - je bent ingelogd als admin.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCostDialogOpen(false)}>Annuleren</Button>
          <Button
            variant="contained"
            onClick={handleConfirmAndProcess}
            disabled={costExceedsLimit && !isAdmin}
          >
            Start
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
